import { Message } from './message';
import { console as reproConsole } from '../utility/console';

type ClickAction = string;
type DisplayCallback = (m: MessageProxyV1) => void;
type ClickCallback = (m: MessageProxyV1, a: ClickAction) => void;

type AutoFlag = boolean | { [k: string]: boolean };
type GaParameter = {
  id: string | undefined;
  category?: string;
  action?: string;
  labelPrefix?: string;
  auto?: AutoFlag;
  namespace?: string;
};

const DEFAULT_EVENT_LABEL_MAP = {
  cta_primary: 'btn1',
  cta_secondary: 'btn2',
  cta_third: 'btn3',
  cta_fourth: 'btn4',
  image_primary: 'img1',
  image_secondary: 'img2',
  image_third: 'img3',
  image_fourth: 'img4',
};

const DEFAULT_AUTO_EVENT = true;

const removeNullishKeys = <T extends {}>(obj: T): Partial<T> => {
  Object.entries(obj).forEach(([k, v]) => {
    if (v == null) delete obj[k as keyof T];
  });
  return obj;
};

export class MessageProxyV1 {
  // 必要なものだけをアクセスできるように注意。
  // アクセスできたらまずそうなものは ECMAScript Private Fields (#fieldName) を使うとよい。
  // また、全ての public method はメッセージスクリプトから呼ばれる可能性があるため、
  // 基本的に引数を unknwon で受け取ってから判定して対処すること
  #message: Message;
  public show: Message['show'];
  public close: Message['close'];
  public el: HTMLElement | null;
  public onShow: DisplayCallback | undefined;
  public onClose: DisplayCallback | undefined;
  public onClick: ClickCallback | undefined;
  #gaTrackerId: GaParameter['id'];
  #gaEventLastTracked: { [k: string]: number };
  #gaAutoEvent: AutoFlag = DEFAULT_AUTO_EVENT;
  #gaEventCategory = 'Repro';
  #gaEventLabelPrefix = '';
  #gaEventAction = '';
  #gaTrackerNamespace = 'Repro';
  #eventNameMap: { [k: string]: string };
  public gaSameEventTrackIntervalMs = 6000;

  constructor(message: Message, dom: MessageDOM) {
    this.show = message.show.bind(message);
    this.close = message.clickClose.bind(message);
    this.#message = message;
    this.el = dom.el;
    if (message.isControlGroup) this.#gaEventLabelPrefix = 'cg_';
    this.#eventNameMap = { ...DEFAULT_EVENT_LABEL_MAP };
    this.#gaEventLastTracked = {};

    message.addCallback('onMessageShow', () => {
      if (this.isGaAutoEventEnabled('view')) this.gaEvent({ label: 'view' });
      if (typeof this.onShow === 'function') this.onShow(this);
    });
    message.addCallback('onMessageClose', () => {
      if (typeof this.onClose === 'function') this.onClose(this);
    });
    message.addCallback('onActionButtonClick', (_, rawClickAction) => {
      const actionsParts = rawClickAction.split(':');
      const clickAction = actionsParts[actionsParts.length - 1];
      const mappedEventName = this.eventNameMap[clickAction] ?? clickAction;
      if (mappedEventName && this.isGaAutoEventEnabled(mappedEventName)) this.gaEvent({ label: mappedEventName });
      if (typeof this.onClick === 'function') this.onClick(this, mappedEventName);
    });
  }

  public get closeOnPagechange() {
    return this.#message.closeOnPagechange;
  }

  public set closeOnPagechange(flag: unknown) {
    if (typeof flag !== 'boolean') {
      reproConsole.error('message.closeOnPagechange: value must be a boolean');
      return;
    }
    this.#message.closeOnPagechange = flag;
  }

  public get apiCompatibilityLevel() {
    return '1' as const;
  }

  // Note: argument is unknwon because this method will be called from message script
  public set ga(params: unknown) {
    if (typeof params !== 'object' || params == null) {
      reproConsole.error("message.ga: argument must be an object (example: {id: 'GA_MEASUREMENT_ID'}).");
      return;
    }

    const { id, category, action, labelPrefix, auto, namespace } = params as { [key: string]: unknown };
    if (!id) {
      reproConsole.error("message.ga: `id` key is required (example: {id: 'GA_MEASUREMENT_ID'}).");
      return;
    }
    if (typeof category !== 'undefined') this.gaEventCategory = category;
    if (typeof action !== 'undefined') this.gaEventAction = action;
    if (typeof labelPrefix !== 'undefined') this.gaEventLabelPrefix = labelPrefix;
    if (typeof auto !== 'undefined') this.gaAutoEvent = auto;
    if (typeof namespace !== 'undefined') this.gaTrackerNamespace = namespace;
    this.gaTrackerId = id;
  }

  public get ga(): GaParameter {
    return {
      id: this.gaTrackerId,
      category: this.gaEventCategory,
      action: this.gaEventAction,
      labelPrefix: this.gaEventLabelPrefix,
      auto: this.gaAutoEvent,
      namespace: this.gaTrackerNamespace,
    };
  }

  // Note: argument is unknwon because this method will be called from message script
  public set gaTrackerId(trackerId: unknown) {
    if (trackerId == null) {
      // Support setting null or undefined to stop event send
      this.#gaTrackerId = undefined;
      return;
    }
    if (typeof trackerId !== 'string') {
      reproConsole.error('message.ga: `id` value must be a string.');
      return;
    }
    if (!trackerId.match(/^(G-[0-9A-Z]+|UA-[0-9]+-[0-9]+)$/)) {
      // Note: ID format spec: https://support.google.com/analytics/answer/7372977
      reproConsole.warn(`message.ga: Your measurement id '${trackerId}' is in incorrect format.`);
    }
    if (trackerId && this.gaTrackerId !== trackerId) {
      if (window.gtag) {
        window.gtag('config', trackerId, { send_page_view: false });
      } else if (window.ga) {
        window.ga('create', trackerId, 'auto', this.#gaTrackerNamespace);
      } else {
        reproConsole.error(
          'message.gaTrackerId: Google alaytics setup failed. Pelase make sure to load Analytics tag.'
        );
      }
    }
    this.#gaTrackerId = trackerId;
  }

  public get gaTrackerId(): GaParameter['id'] {
    return this.#gaTrackerId;
  }

  public set gaAutoEvent(flag: unknown) {
    if (typeof flag === 'boolean') {
      this.#gaAutoEvent = flag;
    } else if (typeof flag === 'object' && flag !== null) {
      const flagObj: { [k: string]: boolean } = {};
      Object.entries(flag).forEach(([k, v]) => {
        if (typeof v !== 'boolean') {
          reproConsole.error(`message.ga['auto']: ignoring non-boolean value for '${k}'`);
          return;
        }
        flagObj[k] = !!v;
      });
      this.#gaAutoEvent = flagObj;
    } else {
      reproConsole.error('message.ga: `auto` value must be a boolean or mapping object.');
    }
  }

  public get gaAutoEvent(): AutoFlag {
    return this.#gaAutoEvent;
  }

  public set gaEventCategory(category: unknown) {
    if (typeof category !== 'string') {
      reproConsole.error('message.ga: `category` value must be a string.');
      return;
    }
    this.#gaEventCategory = category;
  }

  public get gaEventCategory(): GaParameter['category'] {
    return this.#gaEventCategory;
  }

  public set gaEventLabelPrefix(labelPrefix: unknown) {
    if (typeof labelPrefix !== 'string') {
      reproConsole.error('message.ga: `labelPrefix` value must be a string.');
      return;
    }
    this.#gaEventLabelPrefix = labelPrefix;
  }

  public get gaEventLabelPrefix(): GaParameter['labelPrefix'] {
    return this.#gaEventLabelPrefix;
  }

  public set gaEventAction(action: unknown) {
    if (typeof action !== 'string') {
      reproConsole.error('message.ga: `action` value must be a string.');
      return;
    }
    this.#gaEventAction = action;
  }

  public get gaEventAction(): GaParameter['action'] {
    return this.#gaEventAction;
  }

  public set gaTrackerNamespace(namespace: unknown) {
    if (typeof namespace !== 'string') {
      reproConsole.error('message.ga: `namespace` value must be a string.');
      return;
    }
    this.#gaTrackerNamespace = namespace;
  }

  public get gaTrackerNamespace(): GaParameter['namespace'] {
    return this.#gaTrackerNamespace;
  }

  public set eventNameMap(map: unknown) {
    const newMap = {} as { [k: string]: string };
    if (typeof map !== 'object' || map === null) {
      reproConsole.error('eventNameMap must be an object');
      return;
    }
    Object.entries(map).forEach(([k, v]) => {
      if (typeof v !== 'string') {
        reproConsole.error(`eventNameMap: ignoring non-string value for '${k}')`);
        return;
      }
      newMap[k] = v;
    });
    this.#eventNameMap = newMap;
  }

  public get eventNameMap(): { [k: string]: string } {
    return this.#eventNameMap;
  }

  public isGaAutoEventEnabled(action: unknown) {
    if (!this.gaTrackerId) return false;
    if (typeof action !== 'string') return DEFAULT_AUTO_EVENT;
    if (typeof this.gaAutoEvent === 'boolean') return this.gaAutoEvent;
    if (this.gaAutoEvent == null || this.gaAutoEvent[action] == null) return DEFAULT_AUTO_EVENT;
    return this.gaAutoEvent[action];
  }

  public gaEvent({
    category = this.#gaEventCategory,
    action = this.#gaEventAction,
    label: rawLabel,
    value,
    fields,
  }: {
    category?: string;
    action?: string;
    label: string;
    value?: number | undefined;
    fields?: UniversalAnalytics.FieldsObject | undefined;
  }) {
    if (!this.gaTrackerId) {
      reproConsole.error(
        'message.gaEvent: Skipped sending event since tracking ID is not set. You need to set tracking ID before calling gaEvent().'
      );
      return;
    }
    if (!action) {
      reproConsole.error(`messsage gaEvent: Skipped sending GA event (no action value)`);
      return;
    }

    const label = `${this.gaEventLabelPrefix}${this.eventNameMap[rawLabel] ?? rawLabel}`;
    const eid = `${this.gaTrackerId}/${category}/${action}/${label}/${value}/${JSON.stringify(fields)}`;

    if (
      this.#gaEventLastTracked[eid] &&
      new Date().getTime() - this.#gaEventLastTracked[eid] <= this.gaSameEventTrackIntervalMs
    )
      return;

    if (window.gtag) {
      const ev: Gtag.ControlParams | Gtag.EventParams | Gtag.CustomParams = {
        send_to: this.gaTrackerId,
        non_interaction: true,
        event_category: category,
        event_label: label,
        value,
        ...fields,
      };
      removeNullishKeys(ev);
      window.gtag('event', action, ev);
    } else if (window.ga) {
      const payload: UniversalAnalytics.FieldsObject = {
        hitType: 'event',
        nonInteraction: true,
        eventCategory: category,
        eventAction: action,
        eventLabel: label,
        eventValue: value,
        ...fields,
      };
      removeNullishKeys(payload);
      window.ga(`${this.#gaTrackerNamespace}.send`, payload);
    } else {
      reproConsole.error('message.gaEvent: Skipped sending GA event (neither gtag() or ga() were found).');
    }

    this.#gaEventLastTracked[eid] = new Date().getTime();
  }
}
